import { useCallback, useMemo, useState } from "react";
import { defineMessages } from "react-intl";
import { useFormatMessage } from "../Intl/Context";

import Tooltip from "./Tooltip";
import { useSessionState } from "../Utils/useSessionState";
import Button from "../Buttons/Button";
import { useDeferredEffect } from "../Utils/useDeferredEffect";

// This component is used to display feature hints within fixed tooltips to explain some
// features to the users, which are dismissable through a button.
const FeatureTooltip = ({ keepAfterDismissed = false, ...props }) => {
  const { featureName, message, dismissTooltipRef, children, ...passthroughProps } =
    props;

  const t = useFormatMessage();
  const dismissTooltipLabel = t(intlMessages.dismissTooltipLabel);

  // The dismissed state of the tooltip is stored with sessionStorage to avoid displaying
  // the tooltip multiple times to the user during the same browsing session.
  const [isTooltipDismissed, setTooltipDismissed] = useSessionState(featureName, false, {
    deserialize: value => value === "true"
  });

  const dismissTooltip = useCallback(() => {
    setTooltipDismissed(true);
    // This is needed to ensure that the tooltip is actually dismissed, even if
    // keepAfterDismissed is set to true, when the dismiss button is clicked, otherwise
    // you need to hover in and out on the tooltip reference element before it goes away.
    setWaitTimeCompleted(false);
  }, [setTooltipDismissed]);

  // This allows for the parent component to provide a ref and gain the ability to dismiss
  // the tooltip itself, for example if the user did the action that was explained in the
  // feature tooltip, which makes the tooltip useless then.
  if (dismissTooltipRef) {
    dismissTooltipRef.current = dismissTooltip;
  }

  const tooltipContent = useMemo(
    () => (
      <>
        <p>{message}</p>

        {!isTooltipDismissed && (
          <Button
            type="button"
            onClick={dismissTooltip}
            className="btn btn-primary"
            label={dismissTooltipLabel}
          />
        )}
      </>
    ),
    [dismissTooltip, dismissTooltipLabel, isTooltipDismissed, message]
  );

  const [isWaitTimeCompleted, setWaitTimeCompleted] = useState(false);

  useDeferredEffect(
    () => {
      if (!isWaitTimeCompleted) setWaitTimeCompleted(true);
    },
    [isWaitTimeCompleted],
    { wait: 2000 }
  );

  if (!isWaitTimeCompleted) return children;

  // The `keepAfterDismissed` prop allows for keeping the tooltip work as a classic
  // tooltip after it was dismissed the first time through the dismiss button. This is
  // useful to keep the information available without having the tooltip mask useful
  // content when it's not hovered/focused.
  const showTooltip = props.show !== false && (!isTooltipDismissed || keepAfterDismissed);

  return (
    <Tooltip
      {...passthroughProps}
      content={tooltipContent}
      forceDisplay={!isTooltipDismissed}
      show={showTooltip}
      clickable={true}
    >
      {children}
    </Tooltip>
  );
};

export default FeatureTooltip;

const intlMessages = defineMessages({
  dismissTooltipLabel: {
    id: "tooltip.feature.dismissTooltipLabel",
    defaultMessage: "Compris !"
  }
});
